import type { PurchaseRequisition } from '../types/purchasing';
import { formatCostObjectLabel } from './purchasingSpendClass';
import { purchaseListStatus } from './purchasingStatus';

export interface PurchaseSearchHit {
  id: string;
  prNumber: string;
  poNumber?: string;
  vendorName: string;
  statusLabel: string;
  /** Where the query matched: a line, or another requisition field. */
  matchLabel: string;
  matchText: string;
}

interface SearchField {
  label: string;
  text: string;
  line: boolean;
}

function roundPurchaseUsd(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Parses a single query token like `$875`, `875`, or `875.00`. */
export function parsePurchaseSearchDollarToken(token: string): number | null {
  const trimmed = token.replace(/^\$/, '').replace(/,/g, '').trim();
  if (!trimmed || !/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? roundPurchaseUsd(n) : null;
}

function amountsOnRow(row: PurchaseRequisition): number[] {
  const values: number[] = [];
  const add = (value: number | undefined | null) => {
    if (value == null || !Number.isFinite(value)) return;
    values.push(roundPurchaseUsd(value));
  };
  add(row.amount);
  add(row.purchasedAmount);
  for (const line of row.lineItems || []) {
    add((line.quantity ?? 0) * (line.unitPrice ?? 0));
  }
  return values;
}

function amountSearchText(row: PurchaseRequisition): string {
  const parts = new Set<string>();
  amountsOnRow(row).forEach((amount) => {
    parts.add(amount.toFixed(2));
    parts.add(String(amount));
  });
  return Array.from(parts).join(' ');
}

function tokenMatchesRowAmount(token: string, row: PurchaseRequisition): boolean {
  const dollars = parsePurchaseSearchDollarToken(token);
  if (dollars == null) return false;
  return amountsOnRow(row).some((amount) => Math.abs(amount - dollars) < 0.005);
}

function formatAmountHitText(row: PurchaseRequisition, token: string): string {
  const dollars = parsePurchaseSearchDollarToken(token);
  if (dollars != null) {
    const hit = amountsOnRow(row).find((amount) => Math.abs(amount - dollars) < 0.005);
    if (hit != null) return `$${hit.toFixed(2)}`;
  }
  return `$${roundPurchaseUsd(row.amount).toFixed(2)}`;
}

function fieldsFor(row: PurchaseRequisition): SearchField[] {
  return [
    { label: 'PR', text: row.prNumber || '', line: false },
    { label: 'PO', text: row.poNumber || '', line: false },
    { label: 'Amount', text: amountSearchText(row), line: false },
    { label: 'Title', text: row.title || '', line: false },
    { label: 'Vendor', text: row.vendorName || '', line: false },
    { label: 'Requester', text: row.requesterName || '', line: false },
    { label: 'Justification', text: row.justification || '', line: false },
    { label: 'Cost object', text: formatCostObjectLabel(row.costObject), line: false },
    ...((row.lineItems || []).map((line) => ({
      label: 'Line',
      text: line.description || '',
      line: true,
    }))),
  ];
}

function normalizeQueryToken(token: string): string {
  return token.replace(/^\$/, '').replace(/,/g, '').toLowerCase();
}

function tokenHits(text: string, tokens: string[]): number {
  const hay = text.toLowerCase();
  let n = 0;
  tokens.forEach((token) => {
    const norm = normalizeQueryToken(token);
    if (hay.includes(token) || hay.includes(norm)) n += 1;
  });
  return n;
}

/**
 * Line-level search over requisitions the viewer can already see.
 * Status pills are ignored by the caller — pass the full visible set.
 * Every word in the query must appear somewhere on the requisition.
 */
export function searchPurchaseRequisitions(
  rows: PurchaseRequisition[],
  query: string,
): PurchaseSearchHit[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const hits: Array<PurchaseSearchHit & { score: number }> = [];
  rows.forEach((row) => {
    if (!row.id) return;
    const fields = fieldsFor(row);
    const covered = new Set<string>();
    tokens.forEach((token) => {
      if (tokenMatchesRowAmount(token, row)) {
        covered.add(token);
        return;
      }
      const normalized = normalizeQueryToken(token);
      for (const field of fields) {
        const hay = field.text.toLowerCase();
        if (hay.includes(token) || (normalized !== token && hay.includes(normalized))) {
          covered.add(token);
          break;
        }
      }
    });
    if (covered.size < tokens.length) return;

    let best: SearchField | null = null;
    let bestHits = 0;
    for (const field of fields) {
      const n = tokenHits(field.text, tokens);
      if (n === 0) continue;
      const better =
        !best
        || n > bestHits
        || (n === bestHits && field.line && !best.line);
      if (better) {
        best = field;
        bestHits = n;
      }
    }
    if (!best) return;
    const status = purchaseListStatus(row);
    const amountToken = tokens.find((token) => tokenMatchesRowAmount(token, row));
    const matchLabel = amountToken && best.label === 'Amount' ? 'Amount' : best.label;
    const matchText =
      amountToken && (best.label === 'Amount' || tokenMatchesRowAmount(tokens[0] || '', row))
        ? formatAmountHitText(row, amountToken)
        : best.text;
    hits.push({
      id: row.id,
      prNumber: row.prNumber,
      poNumber: row.poNumber,
      vendorName: row.vendorName || '',
      statusLabel: status.label,
      matchLabel,
      matchText,
      score: bestHits + (best.line ? 0.5 : 0) + (amountToken ? 1 : 0),
    });
  });

  hits.sort((a, b) => b.score - a.score || a.prNumber.localeCompare(b.prNumber));
  return hits.slice(0, 40).map(({ score: _score, ...hit }) => hit);
}
